import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Sun, Moon } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage, LANGUAGES } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { getCounterpartPath, isEnglishPath } from '../../utils/i18nRoutes';

// Real Valoriza SVG Logo — extracted from brand assets
const ValorizaLogo = ({ size = 'md' }) => {
  const w = size === 'sm' ? 100 : size === 'lg' ? 180 : 130;
  const ratio = 256.38 / 1047.16;
  const h = Math.round(w * ratio);
  return (
    <svg
      width={w}
      height={h}
      viewBox="0 0 1047.16 256.38"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Valoriza"
      className="transition-colors duration-300"
    >
      <style>{`.st0{fill:currentColor;transition:fill 0.3s}.st1{fill:#8DC21F}.st2{fill:#5A8C1A}`}</style>
      <g className="text-white dark:text-white">
        {/* VALORIZA text paths */}
        <polygon className="st0" points="341.5,72.42 310.56,72.42 284.17,142.32 258.04,72.42 226.25,72.42 268.89,180.18 270.17,183.41 273.64,183.41 293.65,183.41 297.11,183.41 298.39,180.19" />
        <polygon className="st0" points="383.11,72.42 351.98,72.42 376.62,129.69 354.29,129.69 354.29,156.46 387.94,156.46 390.32,162.1 399.24,183.41 431.15,183.41" />
        <polygon className="st0" points="941.52,72.42 910.39,72.42 935.03,129.69 912.7,129.69 912.7,156.46 946.35,156.46 948.73,162.1 957.65,183.41 989.56,183.41" />
        <polygon className="st0" points="470.98,72.42 441.47,72.42 441.47,183.41 514.45,183.41 514.45,156.46 470.98,156.46" />
        <path className="st0" d="M575.63,72.53c-10.53,0-20.29,2.43-28.99,7.21c-8.75,4.81-15.73,11.51-20.75,19.92c-5.03,8.42-7.58,17.87-7.58,28.1c0,10.22,2.55,19.69,7.58,28.16c5.01,8.44,11.98,15.21,20.72,20.12c8.72,4.89,18.48,7.37,29.03,7.37c10.55,0,20.32-2.48,29.04-7.37c8.73-4.9,15.71-11.67,20.72-20.12c5.03-8.47,7.58-17.94,7.58-28.16c0-10.22-2.55-19.67-7.58-28.1c-5.03-8.41-12.01-15.11-20.75-19.92C595.92,74.96,586.16,72.53,575.63,72.53z M575.91,156.46c-5.14,0-9.75-1.24-14.11-3.81c-4.4-2.58-7.82-6-10.44-10.45c-2.61-4.41-3.87-9.13-3.87-14.45c0-5.32,1.24-10.03,3.81-14.39c2.57-4.38,5.97-7.74,10.39-10.27c4.42-2.53,9.07-3.76,14.23-3.76c5.04,0,9.59,1.22,13.91,3.74c4.33,2.53,7.67,5.88,10.2,10.26c2.52,4.37,3.75,9.09,3.75,14.42c0,5.34-1.23,10.09-3.76,14.51c-2.54,4.43-5.88,7.83-10.22,10.4C585.48,155.22,580.94,156.46,575.91,156.46z" />
        <rect className="st0" x="751.45" y="72.42" width="29.51" height="110.99" />
        <polygon className="st0" points="894.82,72.42 799.23,72.42 799.23,99.48 852.65,99.48 799.23,162.75 799.23,183.41 895.54,183.41 895.54,156.34 841.38,156.34 894.82,92.93" />
        <path className="st0" d="M735.08,183.41l-23.05-34.73c10.7-7.62,17.68-20.11,17.68-34.24c0-23.21-18.81-42.02-42.02-42.02h-40.1v26.91h40.1c8.38,0,15.18,6.79,15.18,15.18s-6.79,15.18-15.18,15.18h-40.1v26.77h37.26l15.75,26.95H735.08z" />
        {/* Geometric symbol — 8 arrow paths alternating green */}
        <path className="st1" d="M123.05,99.03v-41.9H109.1v27.94L89.35,65.31l-9.86,9.86l29.63,29.63C113.08,101.75,117.85,99.7,123.05,99.03" />
        <path className="st2" d="M103.85,110.07L74.22,80.44l-9.86,9.86l19.75,19.75H56.17v13.95h41.91C98.75,118.8,100.8,114.03,103.85,110.07" />
        <path className="st1" d="M155.48,124.01h41.91v-13.95h-27.94l19.75-19.75l-9.86-9.86l-29.63,29.63C152.76,114.03,154.81,118.8,155.48,124.01" />
        <path className="st2" d="M144.44,104.8l29.63-29.63l-9.86-9.86l-19.75,19.75V57.13h-13.95v41.91C135.71,99.7,140.48,101.75,144.44,104.8" />
        <path className="st1" d="M98.08,131.46H56.17v13.95H84.1l-19.75,19.75l9.86,9.86l29.64-29.64C100.8,141.43,98.75,136.66,98.08,131.46" />
        <path className="st2" d="M149.7,145.39l29.64,29.64l9.86-9.86l-19.75-19.75h27.94v-13.95h-41.91C154.81,136.66,152.76,141.43,149.7,145.39" />
        <path className="st2" d="M109.12,150.66L79.49,180.3l9.86,9.86l19.75-19.75v27.94h13.95v-41.92C117.85,155.76,113.08,153.71,109.12,150.66" />
        <path className="st1" d="M130.51,156.43v41.92h13.95v-27.94l19.75,19.75l9.86-9.86l-29.64-29.64C140.48,153.71,135.71,155.76,130.51,156.43" />
      </g>
    </svg>
  );
};

function LanguagePicker() {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectLanguage = (code) => {
    setOpen(false);
    // ES/EN have real, indexable routes on some pages — navigate there instead of just
    // swapping the visible text, so the URL and hreflang stay correct.
    if (code === 'en' && !isEnglishPath(location.pathname)) {
      const counterpart = getCounterpartPath(location.pathname);
      if (counterpart) { navigate(counterpart); return; }
    }
    if (code === 'es' && isEnglishPath(location.pathname)) {
      const counterpart = getCounterpartPath(location.pathname);
      navigate(counterpart || '/');
      return;
    }
    setLang(code);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="language-switcher"
        onClick={() => setOpen(!open)}
        className="liquid-glass rounded-full px-3 py-1.5 flex items-center gap-1.5 text-xs font-semibold text-white/80 hover:text-white"
        style={{ border: 'none' }}
      >
        <span>{current.flag}</span>
        <span>{current.label}</span>
        <ChevronDown size={11} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.95 }}
            transition={{ duration: 0.18 }}
            className="absolute right-0 top-full mt-2 rounded-2xl py-1.5 z-50 min-w-[160px]"
            style={{
              background: 'rgba(10,10,10,0.96)',
              border: '1px solid rgba(255,255,255,0.10)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
              backdropFilter: 'blur(12px)',
            }}
          >
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => selectLanguage(l.code)}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left"
                style={{
                  background: l.code === lang ? 'rgba(141,194,31,0.1)' : 'none',
                  color: l.code === lang ? '#8DC21F' : 'rgba(255,255,255,0.7)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <span className="text-base">{l.flag}</span>
                <span className="font-medium">{l.name}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Anchor items scroll within the home page. Route items navigate to a dedicated page.
// If the user is on another route, anchor clicks first route home (with the hash) so
// HomePage's hash-scroll effect can take over.
const NAV_KEYS = [
  { key: 'about', href: '#acerca' },
  { key: 'projects', href: '#proyectos' },
  { key: 'strategy', to: '/estrategia' },
  { key: 'team', to: '/equipo' },
  { key: 'ecosystem', href: '#ecosistema' },
  { key: 'challenges', to: '/retos' },
  { key: 'contact', to: '/contacto' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const scrollTo = (href) => {
    setMobileOpen(false);
    if (location.pathname !== '/') {
      navigate('/' + href);
      return;
    }
    setTimeout(() => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const goHome = (e) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/80 dark:bg-black/80 backdrop-blur-xl border-b border-gray-200/60 dark:border-white/08' : 'bg-transparent'
        }`}
      >
        <div className="v-container">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" onClick={goHome} data-testid="navbar-logo-link" className="flex-shrink-0">
              <ValorizaLogo size="sm" />
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-8">
              {NAV_KEYS.map((item) => (
                item.to ? (
                  <Link
                    key={item.key}
                    to={item.to}
                    className="text-sm font-medium text-gray-600 dark:text-white/60 hover:text-black dark:hover:text-white/90 transition-colors"
                    data-testid={`navbar-link-${item.key}`}
                  >
                    {t(`nav.${item.key}`)}
                  </Link>
                ) : (
                  <button
                    key={item.key}
                    onClick={() => scrollTo(item.href)}
                    className="text-sm font-medium text-gray-600 dark:text-white/60 hover:text-black dark:hover:text-white/90 transition-colors"
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    data-testid={`navbar-link-${item.key}`}
                  >
                    {t(`nav.${item.key}`)}
                  </button>
                )
              ))}
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-3">
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="liquid-glass rounded-full p-2 hover:bg-white/10 dark:hover:bg-white/10 transition-colors"
                style={{ border: 'none' }}
                aria-label="Toggle theme"
                data-testid="theme-toggle"
              >
                {theme === 'dark' ? (
                  <Sun size={18} className="text-white/70 hover:text-white transition-colors" />
                ) : (
                  <Moon size={18} className="text-gray-700 hover:text-black transition-colors" />
                )}
              </button>

              <LanguagePicker />

              <button
                data-testid="navbar-primary-cta"
                onClick={() => { setMobileOpen(false); navigate('/contacto'); }}
                className="hidden sm:flex items-center gap-2 text-sm font-bold text-black rounded-[10px] px-4 py-2 hover:shadow-lg transition-all"
                style={{ background: '#8DC21F', border: 'none', boxShadow: '0 4px 16px rgba(141,194,31,0.3)', cursor: 'pointer' }}
              >
                {t('nav.contact')}
              </button>

              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="md:hidden p-2 text-gray-700 dark:text-white/70"
                data-testid="navbar-mobile-menu-button"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-40 bg-white/96 dark:bg-black/96 flex flex-col pt-20 px-6"
            style={{ backdropFilter: 'blur(24px)' }}
          >
            <div className="mb-6">
              <ValorizaLogo size="md" />
            </div>
            <div className="flex flex-col gap-1">
              {NAV_KEYS.map((item) => (
                item.to ? (
                  <Link
                    key={item.key}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className="text-left text-2xl font-semibold text-gray-800 dark:text-white/80 hover:text-black dark:hover:text-white py-4"
                    style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
                  >
                    {t(`nav.${item.key}`)}
                  </Link>
                ) : (
                  <button
                    key={item.key}
                    onClick={() => scrollTo(item.href)}
                    className="text-left text-2xl font-semibold text-gray-800 dark:text-white/80 hover:text-black dark:hover:text-white py-4"
                    style={{ background: 'none', border: 'none', borderBottom: '1px solid rgba(0,0,0,0.06)', cursor: 'pointer' }}
                  >
                    {t(`nav.${item.key}`)}
                  </button>
                )
              ))}
            </div>
            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="liquid-glass rounded-full p-3"
                style={{ border: 'none' }}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <Sun size={20} className="text-white/70" />
                ) : (
                  <Moon size={20} className="text-gray-700" />
                )}
              </button>
              <LanguagePicker />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
